import { Component, createSignal, For, Show, onMount } from "solid-js";
import { Button, Select, Spinner } from "../ui";
import { scanRepoDocuments, importDocuments } from "../../api/documents";
import type {
  ScannedDocument,
  ImportDocumentsResponse,
  DocumentType,
} from "../../api/types";

export interface OnboardingImportDocsStepProps {
  projectId: string;
  onComplete: () => void;
  onSkip: () => void;
}

type StepState =
  | "scanning"
  | "empty"
  | "select"
  | "importing"
  | "done"
  | "error";

const DOC_TYPE_OPTIONS = [
  { value: "roadmap", label: "Roadmap" },
  { value: "architecture", label: "Architecture" },
  { value: "adr", label: "ADR" },
  { value: "design", label: "Design" },
  { value: "guide", label: "Guide" },
  { value: "custom", label: "Custom" },
];

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const OnboardingImportDocsStep: Component<
  OnboardingImportDocsStepProps
> = (props) => {
  const [stepState, setStepState] = createSignal<StepState>("scanning");
  const [items, setItems] = createSignal<ScannedDocument[]>([]);
  const [selectedPaths, setSelectedPaths] = createSignal<Set<string>>(
    new Set(),
  );
  const [typeOverrides, setTypeOverrides] = createSignal<
    Record<string, DocumentType>
  >({});
  const [result, setResult] = createSignal<ImportDocumentsResponse | null>(
    null,
  );
  const [error, setError] = createSignal<string | null>(null);

  onMount(async () => {
    try {
      const resp = await scanRepoDocuments(props.projectId);
      if (resp.items.length === 0) {
        setStepState("empty");
        return;
      }
      setItems(resp.items);
      const defaults = new Set<string>();
      for (const it of resp.items) {
        if (!it.already_imported) defaults.add(it.path);
      }
      setSelectedPaths(defaults);
      setStepState("select");
    } catch {
      // No linked repo, or scan unavailable — quietly skip this step.
      props.onSkip();
    }
  });

  const togglePath = (path: string) => {
    const next = new Set(selectedPaths());
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setSelectedPaths(next);
  };

  const allSelectableSelected = () => {
    const sel = selectedPaths();
    const all = items();
    if (all.length === 0) return false;
    return all.every((it) => sel.has(it.path));
  };

  const toggleSelectAll = () => {
    if (allSelectableSelected()) {
      setSelectedPaths(new Set<string>());
    } else {
      setSelectedPaths(new Set(items().map((it) => it.path)));
    }
  };

  const effectiveDocType = (it: ScannedDocument): DocumentType =>
    typeOverrides()[it.path] ?? it.doc_type;

  const setOverride = (path: string, docType: DocumentType) => {
    setTypeOverrides({ ...typeOverrides(), [path]: docType });
  };

  const handleImport = async () => {
    const paths = Array.from(selectedPaths());
    if (paths.length === 0) return;
    setStepState("importing");
    setError(null);
    try {
      const overrides: Record<string, DocumentType> = {};
      for (const p of paths) {
        const it = items().find((x) => x.path === p);
        if (!it) continue;
        const chosen = effectiveDocType(it);
        if (chosen !== it.doc_type) overrides[p] = chosen;
      }
      const resp = await importDocuments(props.projectId, {
        paths,
        doc_type_overrides: overrides,
      });
      setResult(resp);
      setStepState("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
      setStepState("error");
    }
  };

  return (
    <div class="h-full flex items-center justify-center p-8">
      {/* Scanning */}
      <Show when={stepState() === "scanning"}>
        <div class="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p class="text-sm text-ink-2">Scanning repository for documents...</p>
        </div>
      </Show>

      {/* Empty — no docs found in repo */}
      <Show when={stepState() === "empty"}>
        <div class="w-full max-w-md text-center">
          <div class="mb-6">
            <div class="w-12 h-12 bg-sunken rounded-md flex items-center justify-center mx-auto mb-4">
              <svg
                class="w-6 h-6 text-ink-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">No documents found</h1>
            <p class="text-ink-2">
              We couldn't find importable markdown in your repo. You can create
              documents directly in Flowicity, or import later from the Docs
              section.
            </p>
          </div>
          <Button
            variant="primary"
            size="lg"
            class="w-full"
            onClick={props.onSkip}
          >
            Continue
          </Button>
        </div>
      </Show>

      {/* Select — import picker */}
      <Show when={stepState() === "select"}>
        <div class="w-full max-w-2xl">
          <div class="text-center mb-6">
            <div class="w-12 h-12 bg-sunken rounded-md flex items-center justify-center mx-auto mb-4">
              <svg
                class="w-6 h-6 text-ink-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">
              We found {items().length} document
              {items().length === 1 ? "" : "s"} in your repo
            </h1>
            <p class="text-ink-2">
              Import them to help the AI understand your project. You can refine
              selections or skip this step.
            </p>
          </div>

          <div class="bg-surface rounded border border-line mb-4">
            <div class="flex items-center justify-between px-4 py-2 border-b border-line">
              <label class="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelectableSelected()}
                  onChange={toggleSelectAll}
                  class="w-4 h-4 accent-ink rounded border-line"
                />
                <span>
                  {selectedPaths().size} of {items().length} selected
                </span>
              </label>
              <span class="text-xs text-ink-3">
                Doc type auto-inferred; edit per row
              </span>
            </div>
            <div class="max-h-80 overflow-y-auto divide-y divide-line">
              <For each={items()}>
                {(it) => (
                  <div class="flex items-start gap-3 px-4 py-3 hover:bg-sunken">
                    <input
                      type="checkbox"
                      checked={selectedPaths().has(it.path)}
                      onChange={() => togglePath(it.path)}
                      class="mt-1 w-4 h-4 accent-ink rounded border-line flex-shrink-0"
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-sm font-medium text-ink break-all">
                          {it.path}
                        </span>
                        <Show when={it.already_imported}>
                          <span class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-sunken text-ink-2 border border-line">
                            Already imported
                          </span>
                        </Show>
                        <span class="text-xs text-ink-3">
                          {formatSize(it.size)}
                        </span>
                      </div>
                      <Show when={it.preview}>
                        <p class="text-xs text-ink-3 mt-1 line-clamp-2">
                          {it.preview}
                        </p>
                      </Show>
                    </div>
                    <div class="w-36 flex-shrink-0">
                      <Select
                        value={effectiveDocType(it)}
                        onChange={(v) =>
                          setOverride(it.path, v as DocumentType)
                        }
                        options={DOC_TYPE_OPTIONS}
                      />
                    </div>
                  </div>
                )}
              </For>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              class="w-full"
              disabled={selectedPaths().size === 0}
              onClick={handleImport}
            >
              Import {selectedPaths().size} document
              {selectedPaths().size === 1 ? "" : "s"}
            </Button>
            <Button
              variant="ghost"
              size="md"
              class="w-full"
              onClick={props.onSkip}
            >
              Skip for now
            </Button>
            <p class="text-xs text-ink-3 text-center">
              You can import later from Docs → Import from GitHub.
            </p>
          </div>
        </div>
      </Show>

      {/* Importing */}
      <Show when={stepState() === "importing"}>
        <div class="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p class="text-sm text-ink-2">
            Importing {selectedPaths().size} document
            {selectedPaths().size === 1 ? "" : "s"}...
          </p>
        </div>
      </Show>

      {/* Done */}
      <Show when={stepState() === "done" ? result() : null}>
        {(r) => (
          <div class="w-full max-w-md text-center">
            <div class="mb-6">
              <div class="w-12 h-12 bg-signal-green-tint rounded-md flex items-center justify-center mx-auto mb-4">
                <svg
                  class="w-6 h-6 text-signal-green"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-ink mb-2">
                Documents imported
              </h1>
              <p class="text-ink-2">
                <span class="font-semibold text-signal-green">
                  {r().imported}
                </span>{" "}
                created
                <Show when={r().updated > 0}>
                  ,{" "}
                  <span class="font-semibold text-signal-blue">
                    {r().updated}
                  </span>{" "}
                  updated
                </Show>
                <Show when={r().failed > 0}>
                  ,{" "}
                  <span class="font-semibold text-signal-red">
                    {r().failed}
                  </span>{" "}
                  failed
                </Show>
              </p>
            </div>
            <Button
              variant="primary"
              size="lg"
              class="w-full"
              onClick={props.onComplete}
            >
              Continue
            </Button>
          </div>
        )}
      </Show>

      {/* Error */}
      <Show when={stepState() === "error"}>
        <div class="w-full max-w-md text-center">
          <div class="mb-6">
            <div class="w-12 h-12 bg-signal-red-tint rounded-md flex items-center justify-center mx-auto mb-4">
              <svg
                class="w-6 h-6 text-signal-red"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">Import failed</h1>
            <p class="text-ink-2">{error() ?? "Something went wrong."}</p>
          </div>
          <div class="flex flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              class="w-full"
              onClick={handleImport}
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="md"
              class="w-full"
              onClick={props.onSkip}
            >
              Skip for now
            </Button>
          </div>
        </div>
      </Show>
    </div>
  );
};
